import React from 'react';
import ReactDOM from 'react-dom';

import setupUFOGlobalErrorHandler from '@atlaskit/react-ufo/setup-ufo-global-error-handler';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { getEmptySessionData } from '@confluence/session-data/entry-points/processSessionData.ts';
import { getSessionDataAndInitializeStatsig } from '@confluence/statsig-client/entry-points/getSessionDataAndInitializeStatsig.ts';
import { ThemeContainer } from '@confluence/theming/entry-points/ThemeContainer.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { loadTranslation } from '@confluence/i18n/entry-points/translation.ts';
import { BrowserNotSupportedLoader as BrowserNotSupportedPage } from '@confluence/browser-not-supported/entry-points/BrowserNotSupportedLoader.tsx';
import { initializePreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { initializeAggRegionSteeringHint } from '@confluence/query-preloader-tools/entry-points/aggRegionSteeringHint.ts';
import { initializeStorageManager } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { applyPersistedStatsigOverrides } from '@confluence/session-data/entry-points/persistedStatsigOverrides.ts';

import { coreBrowserNotSupported } from '@devtools/browser-check';

import { initializeBootstrapStorage } from '../../bootstrapStorageInit.js';
import { initialization } from '../../initialization.js';
import renderComponentIntoDOM from '../StandaloneComponentRenderer/helpers/renderComponentIntoDOM.js';
import { lazyLanguage } from '../../i18n/lazy-language.js';

import { hideMainContainer } from './fix-jank-ui.js';
import StandaloneConfluenceNavigationContainer from './StandaloneConfluenceNavigationContainer.js';

/**
 * setupUFOGlobalErrorHandler MUST be the first thing executed before any rendering operations.
 * This ensures we catch and track any errors that occur during the app's initialization,
 * including async operations and React rendering. Moving this call later would risk missing
 * early errors.
 * it provides ufo:errors:globalCount is the count of all errors which occurred on the client all up
 */
setupUFOGlobalErrorHandler();

const sessionDataPromise = (async () => {
	getApolloClient();
	try {
		const sessionData = await getSessionDataAndInitializeStatsig();
		// Initialize browser storage controls after Statsig is ready (for proper feature gate evaluation)
		// This must happen before initializeStorageManager
		await initializeBootstrapStorage({
			userId: sessionData.userId,
			cloudId: sessionData.cloudId,
		});
		const { userId, isLicensed, monolithRegion } = sessionData;
		initializeAggRegionSteeringHint(monolithRegion);
		initializePreloaderFnContext(sessionData);
		await initializeStorageManager({ userId, isLicensed });
		applyPersistedStatsigOverrides();
		return sessionData;
	} catch {
		// For the same reasons as in the non-standalone case documented in the
		// counterpart of this file which is
		// confluence/packages/confluence-frontend-server/src/index.js at the moment
		return getEmptySessionData();
	}
})();

const translationPromise = loadTranslation(lazyLanguage);

Promise.all([sessionDataPromise, translationPromise]).then(([sessionData]) => {
	const browserNotSupportedCheck = coreBrowserNotSupported();

	// the only requirement for BrowserNotSupported to render is the translationPromise
	if (browserNotSupportedCheck) {
		// the monolith attaches this id to the body here: confluence-core/confluence-webapp/src/main/webapp/decorators/main.vmd:62
		const documentBody = document.getElementById('com-atlassian-confluence');
		// eslint-disable-next-line react/no-deprecated
		return void ReactDOM.render(<BrowserNotSupportedPage />, documentBody);
	}
	initialization(sessionData);
	hideMainContainer();
	renderComponentIntoDOM(
		<ThemeContainer>
			<RoutesContext.Consumer>
				{({ location, matchRoute }) => (
					<StandaloneConfluenceNavigationContainer
						apolloClient={getApolloClient()}
						location={location}
						matchRoute={matchRoute}
					/>
				)}
			</RoutesContext.Consumer>
		</ThemeContainer>,
		'ui-global-sidebar-section',
		true,
	);
});
